import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { 
  Home, ChevronRight, Building2, Heart, Award, Shield,
  ChevronLeft, ChevronRight as ChevronRightIcon, Image, Briefcase
} from "lucide-react";
import { useIsMobile } from "../components/ui/use-mobile";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { ImageWithFallback } from "../components/figma/ImageWithFallback";
import { ScrollArea } from "../components/ui/scroll-area";
import honorImage from "../assets/images/a26d35427798ee8fd47869acb9779883d901ddd4.png";
import honorImage2 from "../assets/images/54db4a1406cc9876d4155399e4fa302e58c51beb.png";
import honorImage3 from "../assets/images/a3d732ec751ba8414f3c4e3e2d4aef50b86d4426.png";
import honorImage4 from "../assets/images/08bae034f28005aa58e4e7679488b236e3bae873.png";
import honorImage7 from "../assets/images/74341be664c51cb58b13f06fa9685fd2db95f175.png";
const companyIntroImage =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8z/C/HwAFgwJ/l7tYJwAAAABJRU5ErkJggg==";
import integrityImage from "../assets/images/0babbc2bcb52e8d66f98273e691160e0c6602a8a.png";
import cultureImage1 from "../assets/images/20260105212153_1409_39.png";
import cultureImage2 from "../assets/images/20260105212145_1407_39.png";
import cultureImage3 from "../assets/images/20260105212149_1408_39.png";
import ywfw1 from "../assets/images/ywfw1.png";
import ywfw2 from "../assets/images/ywfw2.png";
import ywfw3 from "../assets/images/ywfw3.png";
import envImage1 from "../assets/images/zizhizhaopian.jpg";
import envImage2 from "../assets/images/huanjing.jpg";
import envImage3 from "../assets/images/kaibiaohouchangshi.jpg";
import envImage4 from "../assets/images/zizhizhaopian.jpg2.jpg";

// 子页签配置
const tabs = [
  { id: "intro", name: "公司简介", icon: Building2 },
  { id: "environment", name: "环境展示", icon: Image },
  { id: "business", name: "业务范围", icon: Briefcase },
  { id: "culture", name: "企业文化", icon: Heart },
  { id: "honor", name: "荣誉资质", icon: Award },
  { id: "integrity", name: "保密廉洁承诺", icon: Shield },
];

export default function AboutXinyuanPage() {
  const isMobile = useIsMobile();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(tabFromUrl || "intro");
  
  // 当 URL 参数变化时，更新 activeTab
  useEffect(() => {
    if (tabFromUrl && ['intro', 'environment', 'business', 'culture', 'honor', 'integrity'].includes(tabFromUrl)) {
      setActiveTab(tabFromUrl);
    }
  }, [tabFromUrl]);
  const [hoveredTab, setHoveredTab] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<{ src: string; alt: string } | null>(null);
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const [currentMobileIndex, setCurrentMobileIndex] = useState(0);

  // 获取当前年份
  const currentYear = new Date().getFullYear();

  // 按照指定顺序排列荣誉资质照片
  const honorImages = [
    { src: honorImage7, alt: "“中国政府采购网”政府采购代理机构名单" },
    { src: honorImage3, alt: "“四川政府采购网”招标代理机构名单" },
  ];

  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const scrollAmount = scrollContainerRef.current.clientWidth * 0.8;
      const newScrollLeft = direction === 'left' 
        ? scrollContainerRef.current.scrollLeft - scrollAmount
        : scrollContainerRef.current.scrollLeft + scrollAmount;
      
      scrollContainerRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth'
      });
    }
  };

  const scrollMobile = (direction: 'left' | 'right') => {
    let newIndex = currentMobileIndex;
    if (direction === 'left') {
      newIndex = Math.max(0, currentMobileIndex - 1);
    } else {
      newIndex = Math.min(honorImages.length - 1, currentMobileIndex + 1);
    }
    setCurrentMobileIndex(newIndex);
  };

  const openImageDialog = (src: string, alt: string) => {
    setSelectedImage({ src, alt });
  };

  const closeImageDialog = () => {
    setSelectedImage(null);
  };

  const renderContent = () => {
    switch (activeTab) {
      case "intro":
        return <CompanyIntro isMobile={isMobile} />;
      case "environment":
        return <EnvironmentGallery openImageDialog={openImageDialog} isMobile={isMobile} />;
      case "business":
        return <BusinessScope isMobile={isMobile} />;
      case "culture":
        return <CompanyCulture currentYear={currentYear} isMobile={isMobile} />;
      case "honor":
        return <HonorAwards 
          honorImages={honorImages} 
          scroll={scroll}
          scrollMobile={scrollMobile}
          scrollContainerRef={scrollContainerRef}
          currentMobileIndex={currentMobileIndex}
          openImageDialog={openImageDialog}
          isMobile={isMobile}
        />;
      case "integrity":
        return <IntegrityCommitment isMobile={isMobile} />;
      default:
        return <CompanyIntro isMobile={isMobile} />;
    }
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
      <Header />

      {/* 页面横幅 */}
      <div style={{
        background: 'linear-gradient(135deg, #1a365d 0%, #2c5282 50%, #3182ce 100%)',
        padding: isMobile ? '32px 0' : '60px 0',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: '-100px',
          right: '-100px',
          width: '400px',
          height: '400px',
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.05)'
        }} />
        <div style={{
          position: 'absolute',
          bottom: '-150px',
          left: '10%',
          width: '300px',
          height: '300px',
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.03)'
        }} />
        
        <div style={{ width: isMobile ? '90%' : '80%', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '12px' : '16px', marginBottom: isMobile ? '12px' : '16px' }}>
            <Building2 style={{ width: isMobile ? '28px' : '40px', height: isMobile ? '28px' : '40px', color: '#fbbf24' }} />
            <h1 style={{ 
              fontSize: isMobile ? '24px' : '36px', 
              fontWeight: 700, 
              color: 'white',
              textShadow: '0 2px 4px rgba(0,0,0,0.2)'
            }}>
              走进德润慧通
            </h1>
          </div>
          <p style={{ fontSize: isMobile ? '14px' : '16px', color: 'rgba(255,255,255,0.8)' }}>
            专业规范 · 用心服务 · 诚信经营
          </p>
        </div>
      </div>

      {/* 面包屑导航 */}
      <div style={{ backgroundColor: 'white', borderBottom: '1px solid #e5e7eb' }}>
        <div style={{ width: isMobile ? '90%' : '80%', margin: '0 auto', padding: isMobile ? '12px 0' : '16px 0' }}>
          <nav style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '6px' : '8px', fontSize: isMobile ? '12px' : '14px', flexWrap: 'wrap' }}>
            <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#6b7280', textDecoration: 'none' }}>
              <Home style={{ width: isMobile ? '14px' : '16px', height: isMobile ? '14px' : '16px' }} />
              首页
            </Link>
            <ChevronRight style={{ width: isMobile ? '14px' : '16px', height: isMobile ? '14px' : '16px', color: '#d1d5db' }} />
            <span style={{ color: '#2c5282', fontWeight: 500 }}>走进德润慧通</span>
            <ChevronRight style={{ width: isMobile ? '14px' : '16px', height: isMobile ? '14px' : '16px', color: '#d1d5db' }} />
            <span style={{ color: '#1a365d', fontWeight: 500 }}>
              {tabs.find(t => t.id === activeTab)?.name}
            </span>
          </nav>
        </div>
      </div>

      {/* 主内容区域 */}
      <div style={{ width: isMobile ? '90%' : '80%', margin: '0 auto', padding: isMobile ? '20px 0' : '40px 0' }}>
        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? '20px' : '40px' }}>
          {/* 左侧导航 */}
          {!isMobile && (
            <div style={{ width: '240px', flexShrink: 0 }}>
              <div style={{
                backgroundColor: 'white',
                borderRadius: '16px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                overflow: 'hidden',
                position: 'sticky',
                top: '32px'
              }}>
                <div style={{
                  padding: '20px 24px',
                  background: 'linear-gradient(135deg, #1a365d, #2c5282)',
                  color: 'white'
                }}>
                  <h3 style={{ fontSize: '18px', fontWeight: 600 }}>导航菜单</h3>
                </div>
                <div style={{ padding: '12px' }}>
                  {tabs.map((tab) => {
                    const Icon = tab.icon;
                    const isActive = activeTab === tab.id;
                    const isHovered = hoveredTab === tab.id;
                    
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        onMouseEnter={() => setHoveredTab(tab.id)}
                        onMouseLeave={() => setHoveredTab(null)}
                        style={{
                          width: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          padding: '14px 16px',
                          border: 'none',
                          borderRadius: '10px',
                          backgroundColor: isActive 
                            ? '#1a365d' 
                            : isHovered 
                              ? '#f0f9ff' 
                              : 'transparent',
                          color: isActive ? 'white' : '#374151',
                          fontSize: '15px',
                          fontWeight: isActive ? 600 : 400,
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          marginBottom: '4px',
                          textAlign: 'left'
                        }}
                      >
                        <Icon style={{ 
                          width: '18px', 
                          height: '18px',
                          color: isActive ? 'white' : '#6b7280'
                        }} />
                        {tab.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* 移动端顶部标签导航 */}
          {isMobile && (
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              padding: '12px',
              display: 'flex',
              gap: '8px',
              overflowX: 'auto',
              WebkitOverflowScrolling: 'touch'
            }}>
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '8px 14px',
                      border: 'none',
                      borderRadius: '8px',
                      backgroundColor: isActive ? '#1a365d' : '#f3f4f6',
                      color: isActive ? 'white' : '#374151',
                      fontSize: '13px',
                      fontWeight: isActive ? 600 : 400,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      whiteSpace: 'nowrap',
                      flexShrink: 0
                    }}
                  >
                    <Icon style={{ 
                      width: '14px', 
                      height: '14px',
                      color: isActive ? 'white' : '#6b7280'
                    }} />
                    {tab.name}
                  </button>
                );
              })}
            </div>
          )}

          {/* 右侧内容 */}
          <div style={{ flex: 1 }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: isMobile ? '12px' : '16px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              padding: isMobile ? '20px' : '40px',
              minHeight: isMobile ? 'auto' : '600px'
            }}>
              {renderContent()}
            </div>
          </div>
        </div>
      </div>

      {/* Image Zoom Dialog */}
      <Dialog open={!!selectedImage} onOpenChange={(open: boolean) => !open && closeImageDialog()}>
        <DialogContent className="max-w-[95vw] max-h-[95vh] p-0 overflow-hidden bg-black/95 border-none">
          <DialogTitle className="sr-only">{selectedImage?.alt || "荣誉资质证书"}</DialogTitle>
          <DialogDescription className="sr-only">点击查看荣誉资质证书详情</DialogDescription>
          {selectedImage && (
            <div className="relative w-full h-full flex items-center justify-center p-4">
              <img
                src={selectedImage.src}
                alt={selectedImage.alt}
                className="max-w-full max-h-[90vh] object-contain"
              />
              <div className="absolute bottom-4 left-0 right-0 text-center">
                <p className="text-white bg-black/50 backdrop-blur-md px-6 py-3 rounded-lg inline-block">
                  {selectedImage.alt}
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <Footer />
    </div>
  );
}

// 环境展示组件
function EnvironmentGallery({ openImageDialog, isMobile }: { openImageDialog: (src: string, alt: string) => void; isMobile: boolean }) {
  const environmentImages = [
    {
      src: envImage1,
      title: "荣誉展示墙",
      aspectRatio: "9:16" as const,
      column: 1
    },
    {
      src: companyIntroImage,
      title: "前台环境",
      aspectRatio: "16:9" as const,
      column: 2,
      row: 1
    },
    {
      src: envImage3,
      title: "开标候场室",
      aspectRatio: "16:9" as const,
      column: 2,
      row: 2
    },
    {
      src: envImage2,
      title: "评审环境",
      aspectRatio: "16:9" as const,
      column: 2,
      row: 3
    },
    {
      src: envImage4,
      title: "荣誉展示墙",
      aspectRatio: "9:16" as const,
      column: 3
    }
  ];

  return (
    <div>
      <h2 style={{ fontSize: isMobile ? '22px' : '28px', fontWeight: 700, color: '#1a365d', marginBottom: isMobile ? '20px' : '32px', paddingBottom: isMobile ? '12px' : '16px', borderBottom: '3px solid #1a365d' }}>
        环境展示
      </h2>
      
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
        gap: isMobile ? '16px' : '24px',
        marginTop: isMobile ? '20px' : '32px',
        alignItems: 'stretch'
      }}>
        {/* 第一列：左侧照片墙 (9:16) */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', height: '100%' }}>
          <div
            onClick={() => openImageDialog(environmentImages[0].src, environmentImages[0].title)}
            style={{
              position: 'relative',
              borderRadius: '12px',
              overflow: 'hidden',
              cursor: 'pointer',
              height: '100%',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              backgroundColor: 'white'
            }}
          >
            <ImageWithFallback
              src={environmentImages[0].src}
              alt={environmentImages[0].title}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
            />
            <div style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              background: 'rgba(0,0,0,0.6)',
              backdropFilter: 'blur(4px)',
              padding: '12px',
              textAlign: 'center'
            }}>
              <p style={{ color: 'white', fontSize: '14px', fontWeight: 600, margin: 0 }}>
                {environmentImages[0].title}
              </p>
            </div>
          </div>
        </div>

        {/* 第二列：工作环境和开标候场室 (16:9) */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          <div
            onClick={() => openImageDialog(environmentImages[1].src, environmentImages[1].title)}
            style={{
              position: 'relative',
              borderRadius: '12px',
              overflow: 'hidden',
              cursor: 'pointer',
              aspectRatio: '16/9',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              backgroundColor: 'white'
            }}
          >
            <ImageWithFallback
              src={environmentImages[1].src}
              alt={environmentImages[1].title}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
            />
            <div style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              background: 'rgba(0,0,0,0.6)',
              backdropFilter: 'blur(4px)',
              padding: '12px',
              textAlign: 'center'
            }}>
              <p style={{ color: 'white', fontSize: '14px', fontWeight: 600, margin: 0 }}>
                {environmentImages[1].title}
              </p>
            </div>
          </div>
          
          <div
            onClick={() => openImageDialog(environmentImages[2].src, environmentImages[2].title)}
            style={{
              position: 'relative',
              borderRadius: '12px',
              overflow: 'hidden',
              cursor: 'pointer',
              aspectRatio: '16/9',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              backgroundColor: 'white'
            }}
          >
            <ImageWithFallback
              src={environmentImages[2].src}
              alt={environmentImages[2].title}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
            />
            <div style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              background: 'rgba(0,0,0,0.6)',
              backdropFilter: 'blur(4px)',
              padding: '12px',
              textAlign: 'center'
            }}>
              <p style={{ color: 'white', fontSize: '14px', fontWeight: 600, margin: 0 }}>
                {environmentImages[2].title}
              </p>
            </div>
          </div>
          
          <div
            onClick={() => openImageDialog(environmentImages[3].src, environmentImages[3].title)}
            style={{
              position: 'relative',
              borderRadius: '12px',
              overflow: 'hidden',
              cursor: 'pointer',
              aspectRatio: '16/9',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              backgroundColor: 'white'
            }}
          >
            <ImageWithFallback
              src={environmentImages[3].src}
              alt={environmentImages[3].title}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
            />
            <div style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              background: 'rgba(0,0,0,0.6)',
              backdropFilter: 'blur(4px)',
              padding: '12px',
              textAlign: 'center'
            }}>
              <p style={{ color: 'white', fontSize: '14px', fontWeight: 600, margin: 0 }}>
                {environmentImages[3].title}
              </p>
            </div>
          </div>
        </div>

        {/* 第三列：右侧照片墙 (9:16) */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', height: '100%' }}>
          <div
            onClick={() => openImageDialog(environmentImages[4].src, environmentImages[4].title)}
            style={{
              position: 'relative',
              borderRadius: '12px',
              overflow: 'hidden',
              cursor: 'pointer',
              height: '100%',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              backgroundColor: 'white'
            }}
          >
            <ImageWithFallback
              src={environmentImages[4].src}
              alt={environmentImages[4].title}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
            />
            <div style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              background: 'rgba(0,0,0,0.6)',
              backdropFilter: 'blur(4px)',
              padding: '12px',
              textAlign: 'center'
            }}>
              <p style={{ color: 'white', fontSize: '14px', fontWeight: 600, margin: 0 }}>
                {environmentImages[4].title}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// 公司简介组件
function CompanyIntro({ isMobile }: { isMobile: boolean }) {
  return (
    <div>
      <h2 style={{ fontSize: isMobile ? '22px' : '28px', fontWeight: 700, color: '#1a365d', marginBottom: isMobile ? '20px' : '32px', paddingBottom: isMobile ? '12px' : '16px', borderBottom: '3px solid #1a365d' }}>
        公司简介
      </h2>
      
      <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? '20px' : '32px', marginBottom: isMobile ? '24px' : '40px' }}>
        <div style={{ flexShrink: 0, width: isMobile ? '100%' : '400px' }}>
          
        </div>
        <div style={{ flex: 1 }}>
          <ScrollArea style={{ height: isMobile ? 'auto' : '500px', maxHeight: isMobile ? '400px' : '500px' }}>
            <div style={{ paddingRight: '16px' }}>
              <div style={{ fontSize: '16px', lineHeight: 2, color: '#4b5563', textAlign: 'justify' }}>
                <p style={{ marginBottom: '20px' }}>
                  德润慧通工程项目管理（四川）有限公司坐落于成都市高新区新裕路 501 号博雅城市广场 C 座荟创空间 1012 号，是从事招标采购代理专业机构。公司始终秉持 “德正润业，慧达通规” 的核心发展理念，以专业、规范的全流程服务体系，为各级政府机关、企事业单位及社会团体提供高品质的招标采购代理服务。
                </p>
                <p style={{ marginBottom: '20px' }}>
                  自成立以来，公司严格恪守《中华人民共和国招标投标法》《中华人民共和国政府采购法》等国家法律法规，深耕招标采购服务领域，构建起覆盖需求咨询、文件编制、流程管控、争议协调的项目全生命周期服务体系，让招标采购各环节有章可循、规范落地。
                </p>
                <p style={{ marginBottom: '20px' }}>
                  在技术创新与服务升级的道路上，公司自主搭建智能招标管理系统，成功实现电子化招标、大数据分析、风险预警等创新功能，以数字化手段提升招标采购效率与风控能力。凭借专业的服务能力，公司已累计完成多宗政府采购、工程招标、项目咨询服务项目，客户服务满意率达 98% 以上。同时，公司以 “公开、公平、公正、诚信” 为核心执业准则，建立完善的三级质量审核机制，组建专业的专家评审库，打造了一支持有招标师、造价工程师等权威专业资质的技术支撑团队，从人员、机制、资源多维度保障服务质量。
                </p>
                <p>
                  凭借扎实的专业功底与精准的领域深耕，公司在医疗设备采购、消防装备采购、信息化项目代理等细分领域形成显著的专业服务优势，能够针对不同领域项目的特性与需求，提供定制化、精细化的招标采购代理解决方案。
                </p>
              </div>
            </div>
          </ScrollArea>
        </div>
      </div>
    </div>
  );
}

// 业务范围组件
function BusinessScope({ isMobile }: { isMobile: boolean }) {
  const [selectedBusiness, setSelectedBusiness] = useState<number | null>(null);

  const businessItems = [
    {
      id: 1,
      image: ywfw1,
      title: "招标采购代理全流程服务",
      content: {
        title: "招标采购代理全流程服务",
        items: [
          {
            subtitle: "1.采购预算及限价编制",
            text: "依据政府采购政策及项目实际需求，开展市场调研与价格测算，科学编制采购预算；严格按照政策规定设定最高限价，确保预算额度合理、限价精准合规，同时协助完成预算公示相关工作，保障资金使用效益与政策落实。"
          },
          {
            subtitle: "2.采购需求编制",
            text: "遵循\"完整、明确、合规\"原则，结合《政府采购品目分类目录》及项目功能目标，梳理采购标的技术规格、质量安全标准、服务要求、验收标准、交付时限等核心要素；开展需求合规审查，杜绝排斥或限制潜在投标人的条款，形成符合政策要求且贴合实际的采购需求文件。"
          },
          {
            subtitle: "3.采购实施计划编制",
            text: "根据项目规模、技术特性及政策时限要求，制定详细的采购实施进度方案，明确标段划分、招标方式、组织形式、各环节时间节点等核心内容；同步衔接项目审批核准要求，确保计划可落地、全流程可控。"
          },
          {
            subtitle: "4.采购风险审查",
            text: "建立全流程风险防控机制，对照现行政策排查采购活动中的合规风险、廉政风险、流程风险等；制定风险应对预案，出具风险审查报告，保障采购活动合法合规。"
          },
          {
            subtitle: "5.招标采购代理",
            text: "严格按照政策规定开展代理工作：采用标准文本编制资格预审文件、招标文件，发布公告；组织资格预审（或资格后审）、现场考察、答疑会，确保所有潜在投标人信息对称；规范组织开标、评标活动，落实评标专家抽取和评审要求，同步推进电子化政府采购全流程实施，保障交易过程可追溯；完成中标公告、中标通知书发放等工作。"
          },
          {
            subtitle: "6.预签合同审计",
            text: "对照招标文件、中标人投标文件及政策要求，对预签合同条款进行全面审计；重点核查合同条款与采购需求、中标结果的一致性，确保付款方式、履约期限、违约责任、政策扶持条款等符合规定，防范合同风险。"
          },
          {
            subtitle: "7.履约验收",
            text: "协助采购人履行验收主体责任，依据政策要求制定验收方案，明确验收标准、程序及人员组成；组织现场验收，可邀请第三方机构或其他投标人参与；对验收过程进行全程记录，出具验收报告，跟进问题整改，确保中标人履约符合合同约定及政策要求。"
          },
          {
            subtitle: "8.审计应对咨询",
            text: "协助客户整理招标采购全流程资料，对接财政、审计等监管部门的监督检查；提前预判审计重点，制定应对方案，提供专业咨询支持，保障审计工作顺利推进。"
          }
        ]
      }
    },
    {
      id: 2,
      image: ywfw2,
      title: "项目技术咨询",
      content: {
        title: "项目技术咨询",
        items: [
          {
            subtitle: "1.工程咨询",
            text: "聚焦政府投资项目、企业投资项目，提供全流程工程咨询服务：编制项目建议书（含可行性初步分析、政策符合性论证）、可行性研究报告（涵盖技术可行性、经济合理性、环境影响评价、节能评估等核心内容）、初步设计、项目申请报告（对接项目核准要求）、资金申请报告（贴合财政资金支持政策，规范申报材料编制）；协助完成报告评审与审批对接工作，保障项目顺利立项。"
          },
          {
            subtitle: "2.工程造价咨询",
            text: "依据最新政策，提供全流程工程造价服务：编制工程量清单、招标控制价；开展施工阶段造价管控，审核工程进度款、变更签证；进行竣工结算审核，出具合规的造价咨询报告；同步协助落实工程造价政策，保障项目造价合理、资金使用合规。"
          }
        ]
      }
    },
    {
      id: 3,
      image: ywfw3,
      title: "商务咨询",
      content: {
        title: "商务咨询",
        items: [
          {
            subtitle: "1.投标咨询",
            text: "结合\"三盲一共享\"等最新投标政策要求，为投标人提供全流程投标咨询：解读招标文件核心条款、梳理投标资格条件、制定投标策略；协助编制合规的投标文件，指导样品准备；提供模拟评审服务，提升投标文件竞争力。"
          },
          {
            subtitle: "2.质疑、投诉咨询",
            text: "依据《政府采购质疑和投诉办法》等政策，为供应商提供质疑、投诉全流程咨询服务：解读质疑投诉时限要求、指导收集证据材料；协助撰写质疑函、投诉书，对接监管部门处理流程，保障供应商合法权益。"
          },
          {
            subtitle: "3.政策培训咨询",
            text: "聚焦政府采购、招投标领域最新政策，为采购人、投标人等主体定制培训课程；内容涵盖政策核心条款解读、采购流程规范、风险防控要点、主体责任落实等，助力相关人员精准把握政策要求。"
          },
          {
            subtitle: "4.招标采购信息化建设",
            text: "依据《招标投标电子文件归档规范》等政策要求，为客户提供招标采购信息化整体解决方案：协助搭建电子招投标平台，实现招标、投标、开标、评标、归档全流程电子化；对接电子档案管理系统，保障电子文件归档合规（符合\"单套制\"管理要求）；提供系统操作培训与运维支持，提升采购效率与可追溯性。"
          }
        ]
      }
    }
  ];

  const handleImageClick = (index: number) => {
    setSelectedBusiness(index);
  };

  const closeBusinessDialog = () => {
    setSelectedBusiness(null);
  };

  return (
    <>
      <div>
        <h2 style={{ fontSize: isMobile ? '22px' : '28px', fontWeight: 700, color: '#1a365d', marginBottom: isMobile ? '20px' : '32px', paddingBottom: isMobile ? '12px' : '16px', borderBottom: '3px solid #1a365d' }}>
          业务范围
        </h2>
        
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: isMobile ? '16px' : '24px' }}>
          {businessItems.map((item, index) => (
            <div
              key={item.id}
              onClick={() => handleImageClick(index)}
              style={{
                position: 'relative',
                borderRadius: '12px',
                overflow: 'hidden',
                aspectRatio: '16/9',
                cursor: 'pointer',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
              }}
            >
              <ImageWithFallback
                src={item.image}
                alt={item.title}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
              <div style={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                background: 'rgba(255,255,255,0.95)',
                backdropFilter: 'blur(8px)',
                padding: '16px',
                textAlign: 'center'
              }}>
                <p style={{ color: '#1f2937', fontSize: '16px', fontWeight: 600, margin: 0 }}>
                  {item.title}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 业务详情弹窗 - 自定义弹窗 */}
      {selectedBusiness !== null && (
        <>
          {/* 遮罩层 */}
          <div
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              backdropFilter: 'blur(4px)',
              zIndex: 50,
              animation: 'fadeIn 0.2s ease-out'
            }}
            onClick={closeBusinessDialog}
          />
          {/* 弹窗容器 */}
          <div
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '50vw',
              maxWidth: '50vw',
              maxHeight: '90vh',
              backgroundColor: 'white',
              borderRadius: '12px',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              zIndex: 51,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              animation: 'slideIn 0.2s ease-out'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* 标题栏 */}
            <div style={{
              padding: isMobile ? '20px' : '24px 32px',
              borderBottom: '2px solid #e5e7eb',
              background: 'linear-gradient(135deg, #1a365d, #2c5282)',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              position: 'relative'
            }}>
              <h3 style={{
                fontSize: isMobile ? '20px' : '24px',
                fontWeight: 700,
                margin: 0,
                flex: 1
              }}>
                {businessItems[selectedBusiness]?.content.title}
              </h3>
              {/* 自定义关闭按钮 - 无白色边框 */}
              <button
                onClick={closeBusinessDialog}
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '6px',
                  border: 'none',
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background-color 0.2s',
                  outline: 'none',
                  padding: 0,
                  marginLeft: '16px',
                  boxShadow: 'none'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
                }}
                onFocus={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.3)';
                  e.currentTarget.style.outline = 'none';
                  e.currentTarget.style.boxShadow = 'none';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
                }}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>

            {/* 内容区域（可滚动） */}
            <div style={{ 
              flex: 1, 
              overflowY: 'auto',
              maxHeight: 'calc(90vh - 80px)'
            }}>
              <div style={{ padding: isMobile ? '20px' : '32px' }}>
                {businessItems[selectedBusiness]?.content.items.map((contentItem, idx) => (
                  <div
                    key={idx}
                    style={{
                      marginBottom: isMobile ? '24px' : '32px',
                      padding: isMobile ? '20px' : '24px',
                      backgroundColor: '#f8fafc',
                      borderRadius: '12px',
                      borderLeft: '4px solid #1a365d'
                    }}
                  >
                    <h4 style={{
                      fontSize: isMobile ? '16px' : '18px',
                      fontWeight: 600,
                      color: '#1a365d',
                      marginBottom: isMobile ? '12px' : '16px',
                      lineHeight: 1.5
                    }}>
                      {contentItem.subtitle}
                    </h4>
                    <p style={{
                      fontSize: isMobile ? '14px' : '15px',
                      color: '#4b5563',
                      lineHeight: 1.8,
                      margin: 0,
                      textAlign: 'justify'
                    }}>
                      {contentItem.text}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
          {/* 动画样式 */}
          <style>{`
            @keyframes fadeIn {
              from {
                opacity: 0;
              }
              to {
                opacity: 1;
              }
            }
            @keyframes slideIn {
              from {
                opacity: 0;
                transform: translate(-50%, -48%);
              }
              to {
                opacity: 1;
                transform: translate(-50%, -50%);
              }
            }
          `}</style>
        </>
      )}
    </>
  );
}

// 企业文化组件
function CompanyCulture({ currentYear, isMobile }: { currentYear: number; isMobile: boolean }) {
  return (
    <div>
      <h2 style={{ fontSize: isMobile ? '22px' : '28px', fontWeight: 700, color: '#1a365d', marginBottom: isMobile ? '20px' : '32px', paddingBottom: isMobile ? '12px' : '16px', borderBottom: '3px solid #1a365d' }}>
        企业文化
      </h2>
      
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: isMobile ? '16px' : '24px' }}>
        <div style={{ position: 'relative', borderRadius: '12px', overflow: 'hidden', aspectRatio: '16/9' }}>
          <ImageWithFallback
            src={cultureImage1}
            alt="专业规范，用心服务"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
          <div style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            background: 'rgba(255,255,255,0.9)',
            backdropFilter: 'blur(8px)',
            padding: '16px',
            textAlign: 'center'
          }}>
            <p style={{ color: '#1f2937', fontSize: '16px', fontWeight: 500 }}>
              专业规范，用心服务
            </p>
          </div>
        </div>
        <div style={{ position: 'relative', borderRadius: '12px', overflow: 'hidden', aspectRatio: '16/9' }}>
          <ImageWithFallback
            src={cultureImage2}
            alt="团队故事"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
          <div style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            background: 'rgba(255,255,255,0.9)',
            backdropFilter: 'blur(8px)',
            padding: '16px',
            textAlign: 'center'
          }}>
            <p style={{ color: '#1f2937', fontSize: '16px', fontWeight: 500 }}>团队故事</p>
          </div>
        </div>
        <div style={{ position: 'relative', borderRadius: '12px', overflow: 'hidden', aspectRatio: '16/9' }}>
          <ImageWithFallback
            src={cultureImage3}
            alt="以专业赋能"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
          <div style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            background: 'rgba(255,255,255,0.9)',
            backdropFilter: 'blur(8px)',
            padding: '16px',
            textAlign: 'center'
          }}>
            <p style={{ color: '#1f2937', fontSize: '16px', fontWeight: 500 }}>以专业赋能</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// 荣誉资质组件
function HonorAwards({ 
  honorImages, 
  scroll, 
  scrollMobile, 
  scrollContainerRef, 
  currentMobileIndex,
  openImageDialog,
  isMobile
}: {
  honorImages: Array<{ src: string; alt: string }>;
  scroll: (direction: 'left' | 'right') => void;
  scrollMobile: (direction: 'left' | 'right') => void;
  scrollContainerRef: React.RefObject<HTMLDivElement>;
  currentMobileIndex: number;
  openImageDialog: (src: string, alt: string) => void;
  isMobile: boolean;
}) {
  const [isPaused, setIsPaused] = React.useState(false);
  const duplicatedImages = [...honorImages, ...honorImages]; // 复制图片列表以实现无缝循环

  // 自动循环滚动
  useEffect(() => {
    if (!scrollContainerRef.current) return;

    const container = scrollContainerRef.current;
    let scrollInterval: NodeJS.Timeout;

    const autoScroll = () => {
      if (isPaused) return;

      if (container) {
        const scrollAmount = 1; // 每次滚动1px，实现平滑滚动
        const scrollWidth = container.scrollWidth;
        const singleSetWidth = scrollWidth / 2; // 因为图片列表被复制了一次
        
        // 如果滚动到第一组图片的末尾，无缝跳转到开始位置（看起来像是第二组的第一张）
        if (container.scrollLeft >= singleSetWidth - 1) {
          container.scrollLeft = container.scrollLeft - singleSetWidth;
        } else {
          container.scrollLeft += scrollAmount;
        }
      }
    };

    scrollInterval = setInterval(autoScroll, 20); // 每20ms滚动一次

    return () => {
      if (scrollInterval) {
        clearInterval(scrollInterval);
      }
    };
  }, [scrollContainerRef, isPaused]);

  return (
    <div>
      <h2 style={{ fontSize: isMobile ? '22px' : '28px', fontWeight: 700, color: '#1a365d', marginBottom: isMobile ? '20px' : '32px', paddingBottom: isMobile ? '12px' : '16px', borderBottom: '3px solid #1a365d' }}>
        荣誉资质
      </h2>

      {/* Desktop Layout */}
      <div className="hidden md:block" style={{ 
        position: 'relative', 
        height: '600px',
        width: '100%',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        {/* 滚动容器 */}
        <div 
          ref={scrollContainerRef}
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
          style={{
            display: 'flex',
            gap: '16px',
            overflowX: 'auto',
            overflowY: 'hidden',
            height: '100%',
            alignItems: 'center',
            width: '60vw',
            maxWidth: '60vw',
            paddingBottom: '8px',
            scrollbarWidth: 'thin',
            scrollbarColor: '#cbd5e0 #f7fafc',
            boxSizing: 'border-box',
            minWidth: 0
          }}
          className="scrollbar-thin"
        >
          {duplicatedImages.map((image, index) => (
            <div 
              key={index}
              onClick={() => openImageDialog(image.src, image.alt)}
              style={{
                position: 'relative',
                borderRadius: '16px',
                overflow: 'hidden',
                cursor: 'pointer',
                width: '200px',
                height: '540px',
                maxWidth: '200px',
                minWidth: '200px',
                boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                backgroundColor: 'white',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <div style={{ 
                position: 'absolute', 
                inset: 0, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                padding: '8px'
              }}>
                <ImageWithFallback
                  src={image.src}
                  alt={image.alt}
                  style={{ 
                    width: '100%', 
                    height: 'auto', 
                    objectFit: 'contain', 
                    maxHeight: '100%',
                    maxWidth: '100%'
                  }}
                />
              </div>
              <div style={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                background: 'rgba(255,255,255,0.9)',
                backdropFilter: 'blur(8px)',
                padding: '12px',
                textAlign: 'center'
              }}>
                <p style={{ color: '#1f2937', fontSize: '14px', fontWeight: 600, lineHeight: '1.2' }}>
                  {image.alt}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Mobile Layout */}
      <div className="md:hidden" style={{ position: 'relative', height: '500px', padding: '0 48px' }}>
        <button
          onClick={() => scrollMobile('left')}
          disabled={currentMobileIndex === 0}
          style={{
            position: 'absolute',
            left: '8px',
            zIndex: 30,
            backgroundColor: currentMobileIndex === 0 ? '#ccc' : '#003c8c',
            color: 'white',
            borderRadius: '50%',
            padding: '8px',
            border: 'none',
            cursor: currentMobileIndex === 0 ? 'not-allowed' : 'pointer'
          }}
        >
          <ChevronLeft style={{ width: '20px', height: '20px' }} />
        </button>

        <div style={{ display: 'flex', alignItems: 'center', height: '100%', justifyContent: 'center' }}>
          {honorImages.map((image, index) => (
            <div 
              key={index}
              onClick={() => openImageDialog(image.src, image.alt)}
              style={{
                display: index === currentMobileIndex ? 'block' : 'none',
                position: 'relative',
                borderRadius: '16px',
                overflow: 'hidden',
                cursor: 'pointer',
                aspectRatio: '9/16',
                height: '75%',
                boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                backgroundColor: 'white',
                width: '100%',
                maxWidth: '300px',
                margin: '0 auto'
              }}
            >
              <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <ImageWithFallback
                  src={image.src}
                  alt={image.alt}
                  style={{ width: '100%', height: 'auto', objectFit: 'contain', maxHeight: '100%' }}
                />
              </div>
              <div style={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                background: 'rgba(255,255,255,0.9)',
                backdropFilter: 'blur(8px)',
                padding: '12px',
                textAlign: 'center'
              }}>
                <p style={{ color: '#1f2937', fontSize: '14px', fontWeight: 600 }}>
                  {image.alt}
                </p>
              </div>
            </div>
          ))}
        </div>

        <button
          onClick={() => scrollMobile('right')}
          disabled={currentMobileIndex === honorImages.length - 1}
          style={{
            position: 'absolute',
            right: '8px',
            zIndex: 30,
            backgroundColor: currentMobileIndex === honorImages.length - 1 ? '#ccc' : '#003c8c',
            color: 'white',
            borderRadius: '50%',
            padding: '8px',
            border: 'none',
            cursor: currentMobileIndex === honorImages.length - 1 ? 'not-allowed' : 'pointer'
          }}
        >
          <ChevronRightIcon style={{ width: '20px', height: '20px' }} />
        </button>
      </div>
    </div>
  );
}

// 保密廉洁承诺组件
function IntegrityCommitment({ isMobile }: { isMobile: boolean }) {
  return (
    <div>
      <h2 style={{ fontSize: isMobile ? '22px' : '28px', fontWeight: 700, color: '#1a365d', marginBottom: isMobile ? '20px' : '32px', paddingBottom: isMobile ? '12px' : '16px', borderBottom: '3px solid #1a365d' }}>
        保密廉洁承诺
      </h2>
      
      <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? '20px' : '32px', marginBottom: isMobile ? '24px' : '40px' }}>
        <div style={{ flexShrink: 0, width: isMobile ? '100%' : '400px' }}>
          <img
            src={integrityImage}
            alt="保密廉洁承诺"
            style={{ width: '100%', height: 'auto', borderRadius: '12px', objectFit: 'contain' }}
          />
        </div>
        <div style={{ flex: 1 }}>
          <ScrollArea style={{ height: isMobile ? 'auto' : '500px', maxHeight: isMobile ? '400px' : '500px' }}>
            <div style={{ paddingRight: '16px' }}>
              <div style={{ fontSize: '16px', lineHeight: 2, color: '#4b5563', textAlign: 'justify' }}>
                <p style={{ marginBottom: '20px' }}>
                  德润慧通工程项目管理(四川)有限公司始终以公平竞争、诚信经营为核心价值观，坚守对服务对象、全体员工及社会公众的诚信承诺，将廉洁理念深植于企业发展根基。我们坚决维护国家利益与社会公共利益，严守商业道德底线，全力保障采购人与供应商的合法权益，以公平透明的竞争环境、专业优质的服务品质，携手采购人实现共赢发展。
                </p>
                <p style={{ marginBottom: '20px' }}>
                  我们始终明确：公司与服务对象的关系，是纯粹的政企、企事业及企业间合作关系，绝无任何个人利益掺杂其中。全体员工以追求合法合规的公司利润、实现正向个人价值为目标，牢固树立
                  "以廉洁为荣、以贪腐为耻"
                  的职业信念，严格恪守行业规范与企业制度。为践行廉洁承诺，我们向社会公开倡导并严格遵守以下准则：
                </p>
                <p style={{ marginBottom: '12px' }}>
                  1．绝不以向采购人及其亲属输送个人利益的方式，谋求合作机会或特殊待遇；
                </p>
                <p style={{ marginBottom: '12px' }}>
                  2．绝不迷信
                  "关系至上"，始终以专业能力、服务质量赢得信任与合作；
                </p>
                <p style={{ marginBottom: '12px' }}>
                  3．绝不采用虚假宣传、恶意排挤等不正当手段参与市场竞争，维护行业良性秩序；
                </p>
                <p style={{ marginBottom: '12px' }}>
                  4．绝不向采购人提供好处费、回扣、礼金、有价证券等任何形式的财物；
                </p>
                <p style={{ marginBottom: '12px' }}>
                  5．绝不以报销费用、赠送贵重物品、资助私人事项等方式，变相利益输送；
                </p>
                <p style={{ marginBottom: '12px' }}>
                  6．绝不与采购人、供应商恶意串通，规避招标程序或操纵投标结果；
                </p>
                <p style={{ marginBottom: '12px' }}>
                  7．绝不与采购人、供应商私下协商商业秘密、合同核心条款，确保合作全程公开透明；
                </p>
                <p style={{ marginBottom: '20px' }}>
                  8.坚决杜绝员工任何形式的行贿、索贿、受贿行为，对违纪违规行为零容忍。
                </p>
                <p>
                  我们诚挚接受社会各界、服务对象及全体员工的监督，以廉洁立企、以诚信致远，致力于成为值得信赖的服务伙伴！
                </p>
              </div>
            </div>
          </ScrollArea>
        </div>
      </div>
    </div>
  );
}


